import {
  Component,
  Element,
  Event,
  EventEmitter,
  Fragment,
  JSX,
  Prop,
  State,
  h,
} from '@stencil/core'
import { Loader } from '@googlemaps/js-api-loader'

import { Address, StateList } from './air-address.types'
import { SelectOption, SelectOptionValue } from '../air-select-option/air-select-option.types'
import { Status, StatusMessage, Validation } from '../../types/status.types'
import { isUndefined } from '../../utils/helpers'
import { windowProvider } from '../../utils/windowProvider'

enum AddressItems {
  Street = 'street',
  Secondary = 'secondary',
  City = 'city',
  Region = 'region',
  PostalCode = 'postalCode',
  County = 'county',
  Country = 'country',
}

@Component({
  tag: 'air-address',
  styleUrl: 'air-address.scss',
  shadow: true,
})
export class AirAddress {
  /** Reference to the host element */
  @Element() host!: HTMLAirAddressElement

  /** Specifies heading for address form */
  @Prop() label = 'Location'

  /** Specifies address value */
  @Prop({ mutable: true }) value: Address = {
    street: '',
    secondary: '',
    city: '',
    region: '',
    postalCode: '',
    county: '',
    country: 'US',
  }

  /** Specifies autocomplete options */
  @Prop() autocompleteOptions?: google.maps.places.AutocompleteOptions

  /** Specifies validation of address */
  @Prop({ mutable: true }) validation?: Validation

  /** Defines if user see manual form */
  @State() isManual = true

  /** Specifies selected location from autocomplete */
  @State() location?: google.maps.LatLng | google.maps.LatLngLiteral

  /** Emits address value when input was changed */
  @Event({ eventName: 'air-address-change' }) change!: EventEmitter<Address>

  /** Emits address value when it is focused */
  @Event({ eventName: 'air-address-focus' }) focus!: EventEmitter<Address>

  private autocomplete!: google.maps.places.Autocomplete
  private autocompleteInputElement?: HTMLAirInputElement
  private googleMapsApiLoader?: Loader
  private googleMapsApiKey?: string
  private optionalFields: string[] = [AddressItems.County, AddressItems.Secondary]

  componentWillLoad(): void {
    this.googleMapsApiKey = windowProvider().AirConfig?.googleMapsApiKey
    this.isManual = this.googleMapsApiKey === undefined
  }

  async componentDidLoad(): Promise<void> {
    await this.initAutocomplete()
  }

  private async initAutocomplete(): Promise<void> {
    if (this.googleMapsApiKey === undefined) return

    this.googleMapsApiLoader = new Loader({
      apiKey: this.googleMapsApiKey,
      version: 'weekly',
      libraries: ['places'],
    })

    const google = await this.googleMapsApiLoader.load()
    const input = this.autocompleteInputElement?.shadowRoot?.querySelector('input')

    if (!input) {
      throw new Error('Can not find input for autocomplete')
    }

    this.autocomplete = new google.maps.places.Autocomplete(input, {
      ...this.autocompleteOptions,
      componentRestrictions: { country: 'us' },
    })

    this.autocomplete.addListener('place_changed', this.fillInAddress)
  }

  private fillInAddress = (): void => {
    const place = this.autocomplete.getPlace()

    this.value = {
      street: `${this.getAddressInfo(place, 'street_number')} ${this.getAddressInfo(
        place,
        'route',
      )}`,
      city: this.getAddressInfo(place, 'locality') || this.getAddressInfo(place, 'neighborhood'),
      region: this.getAddressInfo(place, 'administrative_area_level_1'),
      postalCode: this.getAddressInfo(place, 'postal_code'),
      county: this.getAddressInfo(place, 'administrative_area_level_2'),
      country: this.getAddressInfo(place, 'country'),
      secondary: '',
    }

    this.location = place.geometry?.location
    this.change.emit(this.value)
  }

  private getAddressInfo(place: google.maps.places.PlaceResult, type: string): string {
    const addressComponents = place?.address_components
    const addressField = addressComponents?.find((field) => field.types.includes(type))
    return addressField?.short_name || ''
  }

  private getStateOptions(): SelectOption[] {
    return StateList.map(({ key, label }) => ({ value: key, label: label }))
  }

  private getFullAddress(): string {
    const { street, city, region } = this.value
    return `${street} ${city} ${region}`.trim()
  }

  private getFieldStatus(field: string): Validation | undefined {
    if (this.validation?.status === 'success') {
      return {
        status: Status.Success,
      }
    }

    if (!this.validation?.fields?.includes(field) || this.optionalFields.includes(field)) {
      return
    }

    return {
      status: Status.Error,
      text: StatusMessage.EmptyField,
    }
  }

  private handleManualChange = (): void => {
    this.isManual = !this.isManual
  }

  private handleAddressFocus = (): void => {
    this.focus.emit(this.value)
  }

  private handleAddressChange = (
    event: CustomEvent<SelectOptionValue | string>,
    addressItemType: AddressItems,
  ): void => {
    this.validation = {
      ...this.validation,
      status: Status.Error,
      // Remove or add address item to the fields array
      fields:
        isUndefined(event.detail) || event.detail === ''
          ? [...(this.validation?.fields || []), addressItemType]
          : this.validation?.fields?.filter((field) => field !== addressItemType),
    }

    this.value[addressItemType] = event.detail.toString()
    this.change.emit(this.value)
  }

  private handleInputStreetChange = (event: CustomEvent<string>): void => {
    this.handleAddressChange(event, AddressItems.Street)
  }

  private handleInputSecondaryChange = (event: CustomEvent<string>): void => {
    this.handleAddressChange(event, AddressItems.Secondary)
  }

  private handleInputCityChange = (event: CustomEvent<string>): void => {
    this.handleAddressChange(event, AddressItems.City)
  }

  private handleSelectRegionChange = (event: CustomEvent<SelectOptionValue>): void => {
    this.handleAddressChange(event, AddressItems.Region)
  }

  private handleInputPostalCodeChange = (event: CustomEvent<string>): void => {
    this.handleAddressChange(event, AddressItems.PostalCode)
  }

  private handleInputCountyChange = (event: CustomEvent<string>): void => {
    this.handleAddressChange(event, AddressItems.County)
  }

  private renderSearchForm(): JSX.Element {
    return (
      <Fragment>
        <div class="search">
          <air-input
            ref={(el?: HTMLAirInputElement): void => {
              this.autocompleteInputElement = el
            }}
            class="form-element autocomplete-input"
            label="Address"
            placeholder="Enter a location"
            value={this.getFullAddress()}
            onAir-focus={this.handleAddressFocus}
          ></air-input>
          <air-input
            class="form-element"
            label="Suite number (Optional)"
            value={this.value.secondary}
            onAir-focus={this.handleAddressFocus}
            onAir-change={this.handleInputSecondaryChange}
            onAir-blur={this.handleInputSecondaryChange}
          ></air-input>
        </div>
        {this.location && <air-map class="map" center={this.location}></air-map>}
      </Fragment>
    )
  }

  private renderManualForm(): JSX.Element {
    return (
      <div class="manual">
        <air-input
          class="form-element street-control"
          value={this.value.street}
          label="Street"
          validation={this.getFieldStatus(AddressItems.Street)}
          onAir-change={this.handleInputStreetChange}
          onAir-focus={this.handleAddressFocus}
          onAir-blur={this.handleInputStreetChange}
        ></air-input>
        <air-input
          class="form-element secondary-control"
          label="Suite number (Optional)"
          value={this.value.secondary}
          validation={this.getFieldStatus(AddressItems.Secondary)}
          onAir-change={this.handleInputSecondaryChange}
          onAir-focus={this.handleAddressFocus}
          onAir-blur={this.handleInputSecondaryChange}
        ></air-input>
        <air-input
          class="form-element city-control"
          value={this.value.city}
          label="City"
          validation={this.getFieldStatus(AddressItems.City)}
          onAir-change={this.handleInputCityChange}
          onAir-focus={this.handleAddressFocus}
          onAir-blur={this.handleInputCityChange}
        ></air-input>
        <air-select
          class="form-element region-control"
          label="State"
          value={this.value.region}
          options={this.getStateOptions()}
          validation={this.getFieldStatus(AddressItems.Region)}
          onAir-change={this.handleSelectRegionChange}
          onAir-focus={this.handleAddressFocus}
          onAir-blur={this.handleSelectRegionChange}
        ></air-select>
        <air-input
          class="form-element postalcode-control"
          value={this.value.postalCode}
          type="zip-code"
          label="Zip Code"
          validation={this.getFieldStatus(AddressItems.PostalCode)}
          onAir-change={this.handleInputPostalCodeChange}
          onAir-focus={this.handleAddressFocus}
          onAir-blur={this.handleInputPostalCodeChange}
        ></air-input>
        <air-input
          class="form-element county-control"
          value={this.value.county}
          label="County (Optional)"
          validation={this.getFieldStatus(AddressItems.County)}
          onAir-change={this.handleInputCountyChange}
          onAir-focus={this.handleAddressFocus}
          onAir-blur={this.handleInputCountyChange}
        ></air-input>
      </div>
    )
  }

  render(): JSX.Element {
    const statusClasses = {
      status: true,
      [`${this.validation?.status}`]: this.validation?.status !== undefined,
    }
    return (
      <div class="address">
        {this.isManual ? this.renderManualForm() : this.renderSearchForm()}
        {this.googleMapsApiKey && (
          <a tabindex="0" class="link" onClick={this.handleManualChange}>
            {this.isManual ? 'Retry address search' : 'Add manually'}
          </a>
        )}
        {this.validation && <div class={statusClasses}>{this.validation.text}</div>}
      </div>
    )
  }
}
